import type { LatLon } from './geo';

const rad = Math.PI / 180;
const deg = 180 / Math.PI;

export interface SolarState {
  /** Declination in degrees. */
  declination: number;
  /** Right ascension in degrees. */
  rightAscension: number;
  /** Greenwich mean sidereal time in degrees. */
  gmst: number;
}

/** Low-precision solar position (about 0.01° here, plenty for skies and a terminator). */
export function solarState(ms: number): SolarState {
  const d = ms / 86_400_000 + 2440587.5 - 2451545.0;
  const g = ((357.529 + 0.98560028 * d) % 360) * rad;
  const q = (280.459 + 0.98564736 * d) % 360;
  const L = (q + 1.915 * Math.sin(g) + 0.02 * Math.sin(2 * g)) * rad;
  const e = (23.439 - 0.00000036 * d) * rad;
  const ra = Math.atan2(Math.cos(e) * Math.sin(L), Math.cos(L)) * deg;
  const dec = Math.asin(Math.sin(e) * Math.sin(L)) * deg;
  const gmst = (((18.697374558 + 24.06570982441908 * d) % 24) + 24) % 24;
  return { declination: dec, rightAscension: (ra + 360) % 360, gmst: gmst * 15 };
}

const norm180 = (x: number) => ((((x + 180) % 360) + 360) % 360) - 180;

/** Point on Earth where the sun is directly overhead. */
export function subsolarPoint(ms: number): LatLon {
  const s = solarState(ms);
  return { lat: s.declination, lon: norm180(s.rightAscension - s.gmst) };
}

export interface SunAtPlace {
  /** Degrees above the horizon (negative below). */
  altitude: number;
  /** Hour angle in degrees, -180..180; negative before solar noon. */
  hourAngle: number;
  /** True while the sun is rising (before local solar noon). */
  morning: boolean;
}

export function sunAt(place: LatLon, ms: number): SunAtPlace {
  const s = solarState(ms);
  const H = norm180(s.gmst + place.lon - s.rightAscension);
  const φ = place.lat * rad;
  const δ = s.declination * rad;
  const alt = Math.asin(Math.sin(φ) * Math.sin(δ) + Math.cos(φ) * Math.cos(δ) * Math.cos(H * rad));
  return { altitude: alt * deg, hourAngle: H, morning: H < 0 };
}

/**
 * Polygon (closed ring of [lon, lat]) covering the night side of the Earth, for drawing the
 * day/night line on maps. Longitudes run -180..180.
 */
export function nightPolygon(ms: number, step = 2): [number, number][] {
  const sub = subsolarPoint(ms);
  const dec = sub.lat * rad;
  const ring: [number, number][] = [];
  // Avoid division by zero at the equinox.
  const tanDec = Math.abs(Math.tan(dec)) < 1e-6 ? 1e-6 * Math.sign(dec || 1) : Math.tan(dec);
  for (let lon = -180; lon <= 180; lon += step) {
    const H = (lon - sub.lon) * rad;
    const lat = Math.atan(-Math.cos(H) / tanDec) * deg;
    ring.push([lon, lat]);
  }
  // Close over the pole that is in darkness.
  const darkPole = sub.lat > 0 ? -90 : 90;
  ring.push([180, darkPole], [-180, darkPole]);
  ring.push(ring[0]!);
  return ring;
}
